import type {
    ActionResponse,
    RenewalResponse,
    Exemplar,
    ExtendedDocument,
    FinishedLendingActionResponse,
    FinishedReturningActionResponse,
    Loan,
    User
} from 'typings/portaro.be.types';
import type {Pageable} from 'typings/portaro.fe.types';
import {ngAsync} from 'shared/utils/ng-@decorators';
import {getActionResponse, transferify, unRestangularize} from 'shared/utils/data-service-utils';
import {extendDocument} from 'shared/utils/document-utils';

export default class LoanDataService {

    public static serviceName = 'loanDataService';
    public static readonly ROUTE = 'loan-items';

    /*@ngInject*/
    constructor(private Restangular: Restangular.IService) {
    }

    @ngAsync()
    @unRestangularize()
    public async getById(loanId: string): Promise<Loan> {
        const response: Loan = await this.Restangular
            .one(LoanDataService.ROUTE, loanId)
            .get();

        extendDocument(response.document as ExtendedDocument);

        return response;
    }

    @ngAsync()
    @unRestangularize()
    public async query(exemplarQ: string, pageable?: Pageable): Promise<Loan[]> {
        let queryParams = {exemplarQ};
        queryParams = pageable ? Object.assign({}, queryParams, pageable) : queryParams;

        const response: Loan[] = await this.Restangular
            .one(LoanDataService.ROUTE)
            .get(queryParams);

        response.forEach((loan) => extendDocument(loan.document as ExtendedDocument));

        return response;
    }

    @ngAsync()
    @unRestangularize()
    public async getByExemplarId(exemplarId: number, pageable?: Pageable): Promise<Loan[]> {
        let queryParams = {recordExemplar: exemplarId};
        queryParams = pageable ? Object.assign({}, queryParams, pageable) : queryParams;

        const response: Loan[] = await this.Restangular
            .one(LoanDataService.ROUTE)
            .get(queryParams);

        response.forEach((loan) => extendDocument(loan.document as ExtendedDocument));

        return response;
    }

    @ngAsync()
    @unRestangularize()
    public async lendExemplar(exemplar: Exemplar, lender: User, quantity?: number): Promise<FinishedLendingActionResponse> {
        try {
            return await this.Restangular
                .one(LoanDataService.ROUTE)
                .post('new-loan', {
                    exemplar: transferify(exemplar, true),
                    lender: transferify(lender, true),
                    quantity
                });
        } catch (httpError) {
            throw getActionResponse(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async returnLoan(loan: Loan): Promise<FinishedReturningActionResponse> {
        try {
            return await this.Restangular
                .all(LoanDataService.ROUTE)
                .all('return')
                .post({loan: loan.id});
        } catch (httpError) {
            throw getActionResponse(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async processLoan(loanId: string, exemplarId: number): Promise<ActionResponse> {
        try {
            return await this.Restangular
                .one(LoanDataService.ROUTE, loanId)
                .all('process')
                .post({
                    exemplar: exemplarId
                });
        } catch (httpError) {
            throw getActionResponse(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async renewLoan(loan: Loan): Promise<RenewalResponse> {
        return this.Restangular
            .one(LoanDataService.ROUTE, loan.id)
            .post('renew');
    }

    @ngAsync()
    @unRestangularize()
    public async renewAllLoans(lender: User): Promise<ActionResponse> {
        return this.Restangular
            .all(LoanDataService.ROUTE)
            .all('renew')
            .post({lender: lender.id, ignoreSomeLoansNotRenewed: true});
    }
}
